import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useIsMobile } from '@/hooks/use-mobile';
import { Input } from "@/components/ui/input";

interface ProductLink {
  id: string;
  name: string;
  desc: string;
}

const productGroups: Record<string, ProductLink[]> = {
  'AI Email': [
    { id: 'coldmail-ai', name: 'ColdMail AI', desc: 'Personalized cold emails' },
    { id: 'follow-up-sequence', name: 'Sequence Writer', desc: 'Sales cadences' },
    { id: 'subject-line-optimizer', name: 'Subject Line Optimizer', desc: 'Score subject lines' },
    { id: 'lifemail', name: 'lifemail', desc: 'AI email with memory' },
    { id: 'inbox-zero-coach', name: 'Inbox Zero Coach', desc: 'Triage inbox' },
  ],
  'Lead Gen': [
    { id: 'linkedin-lead-scraper', name: 'Lead Qualifier', desc: 'LinkedIn scraping' },
    { id: 'local-business-finder', name: 'Local Lead Finder', desc: 'Find local services' },
    { id: 'email-finder-verifier', name: 'Email Verifier', desc: 'Verify deliverability' },
    { id: 'competitor-monitor', name: 'Competitor Monitor', desc: 'Track competitors' },
    { id: 'review-response-generator', name: 'Review Response', desc: 'Reply to reviews' },
  ],
  'Content': [
    { id: 'blog-post-generator', name: 'Blog Post Generator', desc: 'SEO content from briefs' },
    { id: 'linkedin-post-writer', name: 'LinkedIn Post Writer', desc: 'Executive ghostwriting' },
    { id: 'newsletter-builder', name: 'Newsletter Builder', desc: 'Curate from RSS' },
    { id: 'faq-builder', name: 'FAQ Builder', desc: 'Tickets to knowledge base' },
  ],
  'Documents': [
    { id: 'contract-scanner', name: 'Contract Scanner', desc: 'Flag risky clauses' },
    { id: 'resume-parser', name: 'Resume Parser', desc: 'Extract resume data' },
    { id: 'proposal-writer', name: 'Proposal Writer', desc: 'Brief to proposal' },
    { id: 'pdf-summarizer', name: 'PDF Summarizer', desc: 'Summarize PDFs' },
  ],
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [productsOpen, setProductsOpen] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (productsRef.current && !productsRef.current.contains(e.target as Node)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-30 transition-all duration-300 ${scrolled ? 'bg-dark-500/90 backdrop-blur-md shadow-lg border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/Final_logo.png" 
                alt="TaskTide" 
                className="h-9 w-auto object-contain" 
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <span className="text-xl font-bold text-gradient">TaskTide</span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className="text-sm font-medium text-gray-300 hover:text-white px-3 py-2 rounded-md transition-colors">
              Home
            </Link>

            <div className="relative" ref={productsRef}>
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                className="text-sm font-medium text-gray-300 hover:text-white px-3 py-2 rounded-md transition-colors flex items-center gap-1"
              >
                Products <ChevronDown className={`h-3.5 w-3.5 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>

              {productsOpen && (
                <div className="absolute left-0 mt-2 w-[560px] rounded-xl glass-card border border-white/10 shadow-2xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(productGroups).map(([group, items]) => (
                      <div key={group}>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">{group}</div>
                        <div className="space-y-0.5">
                          {items.map((item) => (
                            <Link
                              key={item.id}
                              to={`/projects/${item.id}`}
                              onClick={() => setProductsOpen(false)}
                              className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-dark-300/50 transition-colors"
                            >
                              <span className="text-sm text-gray-300 hover:text-white">{item.name}</span>
                              <span className="text-xs text-gray-500">{item.desc}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <Link
                      to="/projects"
                      onClick={() => setProductsOpen(false)}
                      className="text-sm text-tasktide-teal hover:text-white transition-colors flex items-center gap-1"
                    >
                      View all 28 products →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className="relative group">
              <button className="text-sm font-medium text-gray-300 hover:text-white px-3 py-2 rounded-md transition-colors flex items-center gap-1">
                About <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div className="absolute left-0 mt-2 w-48 rounded-xl glass-card border border-white/10 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                <Link to="/about" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-300/50 transition-colors">Company</Link>
                <Link to="/about/mission" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-300/50 transition-colors">Mission</Link>
                <Link to="/about/technology" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-dark-300/50 transition-colors">Technology</Link>
              </div>
            </div>

            <Link to="/contact" className="text-sm font-medium text-gray-300 hover:text-white px-3 py-2 rounded-md transition-colors">
              Contact
            </Link>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="text-gray-400 hover:text-white ml-2"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-white p-2">
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-4 py-4 space-y-1 glass-card border-t border-white/5">
          <Link to="/" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-white hover:bg-dark-300/50">Home</Link>
          <Link to="/projects" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-tasktide-teal hover:bg-dark-300/50">All Products</Link>
          <Link to="/about" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-white hover:bg-dark-300/50">About</Link>
          <Link to="/about/mission" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-400 hover:bg-dark-300/50">  Mission</Link>
          <Link to="/about/technology" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-400 hover:bg-dark-300/50">  Technology</Link>
          <Link to="/contact" onClick={() => setIsOpen(false)} className="block px-3 py-2.5 rounded-md text-base font-medium text-white hover:bg-dark-300/50">Contact</Link>
          <form onSubmit={handleSearchSubmit} className="flex gap-2 pt-2">
            <Input
              type="text"
              placeholder="Search..."
              className="bg-dark-400/50 border-dark-300 text-white"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button type="submit" variant="ghost" size="icon">
              <Search className="h-4 w-4 text-white" />
            </Button>
          </form>
        </div>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-start justify-center pt-24 px-4">
          <div className="w-full max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-medium text-white">Search</h2>
              <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="text-white">
                <X className="h-6 w-6" />
              </Button>
            </div>
            <form onSubmit={handleSearchSubmit} className="relative">
              <Input
                type="text"
                placeholder="Search products, pages..."
                className="bg-dark-400/50 border-dark-300 text-white pr-10 py-6 text-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <Button
                type="submit"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white"
              >
                <Search className="h-5 w-5" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;